/**
 * Shared AudioContext singleton — used by tradeAudio and defenseAudio.
 *
 * Single AudioContext avoids browser limits on concurrent contexts.
 * Provides playTone() for simple sine waves and playSynthSound() for
 * multi-voice synthesized sounds with envelope shaping.
 */

import { boundaryWarn, boundaryError } from '../infra/boundaryLog';
import { readSoundSettings } from './soundSettings';

export interface SynthVoice {
  type: OscillatorType | 'noise';
  freq: number;
  duration: number;
  amplitude: number;
  detune?: number;
  /** Sweep target frequency over duration */
  freqSweep?: number;
  /** Attack time in seconds (default 0.01) */
  attack?: number;
  /** Decay time in seconds — exponential ramp to silence (default = duration) */
  decay?: number;
  /** Delay before starting this voice (seconds, default 0) */
  delay?: number;
}

/**
 * Scale every voice's amplitude by the PER-CHANNEL multiplier.
 *
 * Introduced by #233 for the four Position Tracker tones and lifted here by
 * #232, which needed it for `playBootTone` in the sibling `defenseAudio`
 * module too. It lives beside `SynthVoice` because that is the shape it
 * transforms, and both tone modules already import from here — the
 * alternative, `defenseAudio` importing `tradeAudio`, inverts sibling
 * layering for nothing.
 *
 * Channel multiplier ONLY — `playSynthSound` applies master volume itself, so
 * passing the effective (master x channel) product would apply master twice.
 * Returns the array untouched at the 1.0 default, keeping shipped tones
 * bit-identical, and fails safe to 1.0 on garbage.
 */
export function atChannelVolume(channelVolume: number) {
  return <T extends { amplitude: number }>(voices: T[]): T[] => {
    if (!Number.isFinite(channelVolume) || channelVolume < 0 || channelVolume === 1) return voices;
    return voices.map((voice) => ({ ...voice, amplitude: voice.amplitude * channelVolume }));
  };
}

let audioCtx: AudioContext | null = null;

function ensureContext(): AudioContext | null {
  try {
    if (!audioCtx) {
      const Ctor =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      audioCtx = new Ctor();
    }
    return audioCtx;
  } catch {
    return null;
  }
}

// ── Pending sound drain — replays fill sounds that arrive before audio unlock ──

const MAX_PENDING_SOUNDS = 8;
let _pendingSounds: Array<() => void> = [];

/** Queue a sound to play on next successful AudioContext resume (user gesture).
 *  After a refresh, Chrome/Safari suspend Web Audio until the first gesture.
 *  Fills can still arrive during that locked window, so keep a small FIFO
 *  instead of overwriting the previous pending fill. */
export function setPendingSound(fn: () => void): void {
  _pendingSounds.push(fn);
  if (_pendingSounds.length > MAX_PENDING_SOUNDS) {
    _pendingSounds = _pendingSounds.slice(-MAX_PENDING_SOUNDS);
  }
}

function drainPendingSound(): void {
  if (_pendingSounds.length === 0) return;
  const queued = _pendingSounds;
  _pendingSounds = [];
  queued.forEach((fn, index) => {
    window.setTimeout(fn, index * 180);
  });
}

// @ai-note Eager warmup — creates and resumes AudioContext on the FIRST user
// gesture (click/key/touch) so it's already running when a polling-detected
// fill tries to play a sound. Without this, Chrome's autoplay policy silently
// blocks all sounds until a gesture occurs.
function warmupOnGesture() {
  const ctx = ensureContext();
  if (ctx && ctx.state === 'suspended') {
    // A rejected resume is a normal blocked-browser outcome, not a fault: swallow
    // it so the warmup cannot surface as an unhandled rejection. Draining is
    // skipped in that case exactly as before — the queue waits for the next gesture.
    ctx.resume().then(drainPendingSound, () => {});
  } else {
    drainPendingSound();
  }
  // Keep listening — Chrome can re-suspend after tab backgrounding
}
document.addEventListener('pointerdown', warmupOnGesture);
document.addEventListener('click', warmupOnGesture);
document.addEventListener('keydown', warmupOnGesture);
document.addEventListener('touchstart', warmupOnGesture);
// @ai-note Chrome suspends AudioContext when tab is backgrounded. Resume on tab-switch-back
// even without a click/key gesture so fill sounds play immediately on return.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') warmupOnGesture();
});

export function getAudioContext(): AudioContext | null {
  return ensureContext();
}

/** Check if AudioContext is ready to play sounds immediately. */
export function isAudioReady(): boolean {
  return audioCtx?.state === 'running';
}

/**
 * How long {@link ensureAudioReady} waits on `resume()` before it stops waiting
 * and reads the state instead. Generous next to a real unlock — on a genuine
 * user gesture the resume settles within the same frame — and short enough that
 * a blocked browser answers the trader rather than spinning.
 */
const RESUME_PROBE_TIMEOUT_MS = 1200;

/**
 * What a readiness probe can conclude.
 *
 * `blocked` deliberately covers BOTH "the browser is withholding audio" and
 * Safari's `interrupted` (another app took the output). The recovery is the
 * same gesture either way, and a state we cannot name is safer reported as
 * blocked than as ready.
 */
export type AudioReadiness = 'ready' | 'blocked' | 'unavailable';

/**
 * Resume the shared context from the caller's user gesture and report, TRUTHFULLY,
 * whether a tone played now would be audible.
 *
 * ── Why this cannot just await `resume()` ────────────────────────────────
 * Measured 2026-09-02 on a context created without a gesture, in BOTH target
 * browsers — Chrome, and the Safari the incident was reported on:
 *
 *     stateAtCreation:  "suspended"
 *     resumeOutcome:    STILL-PENDING-AFTER-3s
 *     stateAfterResume: "suspended"
 *
 * Safari 26.6 behaves identically to Chrome here; it does NOT reject.
 *
 * A blocked `resume()` does not reject and does not resolve — **it stays
 * pending indefinitely**, and settles retroactively whenever a gesture finally
 * arrives (the same promise resolved the instant a real click landed). So
 * `await ctx.resume()` on a blocked browser never returns at all: that is why
 * `playSynthSound` goes silent with nothing in the console on such a machine,
 * and why a readiness check built on `await` would hang instead of answering.
 *
 * A browser could instead reject, or resolve while the state stays suspended —
 * neither has been OBSERVED here, so neither is claimed as fact; they are simply
 * shapes this cannot afford to be wrong about. One rule covers all of them: race
 * the resume against a bounded timeout, then believe `ctx.state` and nothing
 * else. The state is the only signal that answers the real question — is a tone
 * audible now — without depending on which shape the browser picked.
 *
 * Playing the tone is deliberately NOT this function's job. The caller plays it
 * only on `ready`, which is what keeps a "sound played" claim honest.
 */
export async function ensureAudioReady(
  timeoutMs: number = RESUME_PROBE_TIMEOUT_MS
): Promise<AudioReadiness> {
  const ctx = ensureContext();
  // The constructor threw — no Web Audio on this browser at all.
  if (!ctx) return 'unavailable';
  if (ctx.state === 'running') return 'ready';
  // A closed context never resumes; resuming one rejects. Nothing to recover.
  if (ctx.state === 'closed') return 'unavailable';

  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      // Swallowed here, not caught around the race: a rejection that lands
      // AFTER the timeout has already settled the race would otherwise become
      // an unhandled rejection with no one left to receive it.
      ctx.resume().catch(() => undefined),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, timeoutMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }

  // Re-read rather than trust how the race ended: resolved, rejected and timed
  // out all reduce to the same question, and only the state answers it.
  //
  // Deliberately through `isAudioReady()` rather than `ctx.state` directly.
  // Narrowing from the guards above insists this can only be 'suspended' |
  // 'interrupted' — which is exactly the thing the timeout just tried to
  // change: `state` mutates asynchronously, so the compiler's view of it is
  // stale by construction. Reading through the helper asks the question fresh,
  // and keeps "what counts as ready" stated in one place.
  return isAudioReady() ? 'ready' : 'blocked';
}

// Sound health event emitter — lets the header show play/error flash
export type SoundHealthStatus = 'ok' | 'error';
const soundHealthListeners = new Set<(status: SoundHealthStatus) => void>();
export function onSoundHealth(cb: (status: SoundHealthStatus) => void): () => void {
  soundHealthListeners.add(cb);
  return () => soundHealthListeners.delete(cb);
}
function emitSoundHealth(status: SoundHealthStatus) {
  soundHealthListeners.forEach((cb) => cb(status));
}

function getMasterVolume(): number {
  return readSoundSettings().volume;
}

export async function playTone(
  freqHz: number,
  durationSec: number,
  amplitude: number
): Promise<void> {
  const ctx = getAudioContext();
  if (!ctx) {
    boundaryError('sound', 'playTone: no AudioContext', { freqHz, durationSec, amplitude });
    return;
  }
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch (err) {
      boundaryError('sound', 'playTone: resume failed', { freqHz, error: String(err) });
      return;
    }
  }
  const masterVol = getMasterVolume();
  const scaled = amplitude * masterVol;
  if (scaled < 0.001) return; // effectively silent
  boundaryWarn('sound', 'playTone', { freqHz, durationSec, amplitude, masterVol });
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.frequency.value = freqHz;
  osc.type = 'sine';
  gain.gain.setValueAtTime(scaled, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + durationSec);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + durationSec);
}

/**
 * Play multiple synthesized voices simultaneously with envelope shaping.
 * Each voice gets its own oscillator + gain node routed to the destination.
 */
export async function playSynthSound(voices: SynthVoice[]): Promise<void> {
  const ctx = getAudioContext();
  if (!ctx) {
    boundaryError('sound', 'playSynthSound: no AudioContext', { voiceCount: voices.length });
    emitSoundHealth('error');
    return;
  }
  if (ctx.state === 'suspended') {
    try {
      await ctx.resume();
    } catch (err) {
      boundaryError('sound', 'playSynthSound: resume failed', { error: String(err) });
      emitSoundHealth('error');
      return;
    }
  }
  const master = getMasterVolume();
  if (master < 0.001) return;
  boundaryWarn('sound', 'playSynthSound', { voiceCount: voices.length, masterVol: master });
  emitSoundHealth('ok');

  const outcome = scheduleSynthVoices(ctx, voices, master);
  if (outcome.kind === 'partial' || (outcome.kind === 'not-started' && outcome.reason === 'failed')) {
    emitSoundHealth('error');
  }
}

/** Scheduling evidence, never a claim that speakers were audible. A partial
 * start consumes the attempt under #806; it must not replay a whole chime.
 */
export type SynthAttemptOutcome =
  | { kind: 'not-started'; reason: 'blocked' | 'unavailable' | 'muted' | 'fenced' | 'failed' }
  | { kind: 'scheduled'; startedVoices: number }
  | { kind: 'partial'; startedVoices: number };

/** Report an explicit attempt once, without claiming speaker audibility.
 * Muted/fenced attempts are intentional silence rather than audio failures. */
export function reportSynthAttemptHealth(outcome: SynthAttemptOutcome): void {
  if (outcome.kind === 'scheduled') {
    boundaryWarn('sound', 'synth attempt scheduled', { startedVoices: outcome.startedVoices });
    emitSoundHealth('ok');
  } else if (outcome.kind === 'partial' || outcome.reason === 'failed' || outcome.reason === 'unavailable') {
    boundaryError('sound', 'synth attempt incomplete', { ...outcome });
    emitSoundHealth('error');
  } else if (outcome.reason === 'blocked') {
    boundaryWarn('sound', 'synth attempt blocked');
  }
}

/** #806 adapter for a coordinator-owned fresh attempt. Synchronous by design:
 * never wait on resume(), enqueue playback, or leave a late callback capable of
 * sounding after the ownership lease has been handed to another tab.
 * The current fill path does not use this API until lifecycle integration.
 */
export function trySynthSoundNow(voices: SynthVoice[], canStart: () => boolean): SynthAttemptOutcome {
  if (!canStart()) return { kind: 'not-started', reason: 'fenced' };
  const ctx = getAudioContext();
  if (!ctx || ctx.state === 'closed') return { kind: 'not-started', reason: 'unavailable' };
  if (ctx.state !== 'running') return { kind: 'not-started', reason: 'blocked' };
  const master = getMasterVolume();
  if (!Number.isFinite(master) || master < 0.001 || !voices.some(v => v.amplitude * master >= 0.001)) {
    return { kind: 'not-started', reason: 'muted' };
  }
  if (!canStart()) return { kind: 'not-started', reason: 'fenced' };
  return scheduleSynthVoices(ctx, voices, master, canStart);
}

function scheduleSynthVoices(ctx: AudioContext, voices: SynthVoice[], master: number,
  canStart: () => boolean = () => true): SynthAttemptOutcome {
  const now = ctx.currentTime;
  let startedVoices = 0;
  const nodes: AudioNode[] = [];
  const cleanup = () => { for (const node of nodes) { try { node.disconnect(); } catch { /* best effort */ } } };

  try {
  for (const v of voices) {
    const scaled = v.amplitude * master;
    if (scaled < 0.001) continue;

    const offset = v.delay ?? 0;
    const gain = ctx.createGain();
    nodes.push(gain);
    gain.connect(ctx.destination);

    const attack = v.attack ?? 0.01;
    const decay = v.decay ?? v.duration;

    // Envelope: ramp up during attack, then exponential decay
    gain.gain.setValueAtTime(0.001, now + offset);
    gain.gain.linearRampToValueAtTime(scaled, now + offset + attack);
    gain.gain.exponentialRampToValueAtTime(0.001, now + offset + attack + decay);

    if (v.type === 'noise') {
      // White noise via buffer source
      const bufferSize = ctx.sampleRate * v.duration;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }
      const noise = ctx.createBufferSource();
      nodes.push(noise);
      noise.buffer = buffer;
      noise.connect(gain);
      if (!canStart()) { cleanup(); return startedVoices > 0 ? { kind: 'partial', startedVoices }
        : { kind: 'not-started', reason: 'fenced' }; }
      noise.start(now + offset);
      startedVoices++;
      noise.stop(now + offset + v.duration);
    } else {
      const osc = ctx.createOscillator();
      nodes.push(osc);
      osc.type = v.type;
      osc.frequency.setValueAtTime(v.freq, now + offset);
      if (v.detune) osc.detune.setValueAtTime(v.detune, now + offset);
      if (v.freqSweep) {
        osc.frequency.exponentialRampToValueAtTime(v.freqSweep, now + offset + v.duration);
      }
      osc.connect(gain);
      if (!canStart()) { cleanup(); return startedVoices > 0 ? { kind: 'partial', startedVoices }
        : { kind: 'not-started', reason: 'fenced' }; }
      osc.start(now + offset);
      startedVoices++;
      osc.stop(now + offset + v.duration);
    }
  }
  } catch {
    cleanup();
    return startedVoices > 0 ? { kind: 'partial', startedVoices }
      : { kind: 'not-started', reason: 'failed' };
  }
  return startedVoices > 0 ? { kind: 'scheduled', startedVoices }
    : { kind: 'not-started', reason: 'muted' };
}
